<template>
  <div class="message-composer-container">
    <div class="message-composer__error"
         v-if="withAnotherAdviser">
      {{ $t('general.conversationAlreadyTaken') }}
    </div>
    <div v-else-if="showActionsButton"
         @click="takeConversation()"
         class="message-composer__button-actions"
         v-ripple>
      <v-icon color="white"
              left>
        check
      </v-icon>
      <span v-if="mustSendTemplate">{{$t('general.startConversation')}}</span>
      <span v-else-if="isEnded">{{ $t('general.toWrite') }}</span>
      <span v-else-if="withBot">{{ $t('general.toHelp') }}</span>
      <span v-else>{{ $t('general.attendConversation') }}</span>
    </div>
    <div v-else style="position: relative; display: flex; width: 100%; align-items: center;">
      <div class="kb-textarea-container"
           :class="recording ? 'noWritingAllow' : 'allow'">
        <div class="kb-textarea"
             :data-ph="$t('general.writeYourMessageHere')"
             contenteditable="true"
             aria-placeholder="texto"
             @input="updateTextLength($event)"
             @keydown.enter.prevent
             @keydown="checkKeyDown($event)"
             @keyup.enter="sendMessage()"
             ref="messageEditor"
             @paste="pasteText">
        </div>
      </div>
      <span v-if="chatSettings.quickResponses.length > 0 && !recording">
        <v-menu :close-on-content-click="false"
                left
                max-width="400px"
                style="width: 400px"
                top
                v-model="menuQuickResponses">
          <template v-slot:activator="{ on }">
              <v-icon class="ml-2 grey--text"
                      v-on="on">
                  mdi-clock-fast
              </v-icon>
          </template>
          <v-card>
            <v-text-field clearable
                          flat
                          hide-details
                          :placeholder="$t('general.findQuickAnswer')"
                          solo
                          v-model="searchQuickResponses">
            </v-text-field>
            <v-divider/>
            <v-card-text style="max-height: 300px; overflow-y: auto">
              <v-row no-gutters>
                <v-col cols="12"
                       class="text-center"
                       v-if="quickResponses.length === 0">
                  <v-icon color="error">
                    mdi-alert-circle
                  </v-icon>
                  {{$t('general.noAnswerFound')}}
                </v-col>
                <v-col cols="12"
                       :key="option"
                       v-for="option in quickResponses">
                  <CustomChip @click="sendMessage(option)"
                              :hide-actions="true">
                    {{ option }}
                  </CustomChip>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-menu>
      </span>
      <v-icon class="ml-2 grey--text"
              @click="$emit('attachFile')"
              v-if="!recording">
        mdi-paperclip
      </v-icon>
      <div style="display:contents">
        <v-icon class="mdi mdi-telegram mdi-24px cursor-pointer"
                :class="[conversationsIsConnected ? 'grey--text' : 'red--text']"
                @click="sendMessage()"
                style="padding: 15px"
                v-if="textLength > 0">
        </v-icon>
        <v-icon class="mdi mdi-microphone mdi-24px pointer grey--text pointer"
                @click="recording = true"
                style="padding: 15px"
                v-if="!recording && textLength <= 0">
        </v-icon>
        <AudioRecorder :recording="recording"
                       @send="uploadAudio"
                       @stop="recording = false"
                       v-model="audio"
                       v-show="recording">
        </AudioRecorder>
      </div>
      <div class="error--text"
           style="bottom: 0; right: 16px; position: absolute; font-size: 12px"
           v-if="textLength > 1900">
        {{textLength}} / {{settings.maxLen}}
      </div>
    </div>
  </div>
</template>

<script>
import AudioRecorder from '@/pages/App/Conversations/components/AudioRecorder.vue'
import CustomChip from '@/pages/App/Conversations/components/CustomChip.vue'
import conversationsService from '@/services/conversationsService.js'
import { EventBus } from '@/eventBus.js'
import { mapGetters, mapState } from 'vuex'
import notifications from '@/libs/notifications'

export default {
  components: { AudioRecorder, CustomChip },
  computed: {
    ...mapGetters(['chatSettings', 'conversationsIsConnected']),
    ...mapState({
      drafts: state => state.conversations.drafts,
      mustSendTemplate: state => state.conversations.mustSendTemplate,
      roomInfo: state => state.conversations.roomInfo,
      selectedRoom: state => state.conversations.selectedRoom,
      socket: state => state.chat.socket,
      userInfo: state => state.conversations.user,
      userKeybe: state => state.auth.userKeybe
    }),
    isEnded () {
      return this.roomInfo.state === 'ended'
    },
    quickResponses () {
      if (!this.searchQuickResponses) return this.chatSettings.quickResponses
      const search = this.searchQuickResponses.toLowerCase()
      return this.chatSettings.quickResponses.filter(item => {
        return item.toLowerCase().includes(search)
      })
    },
    showActionsButton () {
      const state = this.roomInfo.state
      return this.availableStates.includes(state) || this.mustSendTemplate
    },
    withAnotherAdviser () {
      return this.roomInfo.state === 'withAdvisor' && this.roomInfo.advisorId !== this.userKeybe.userId
    },
    withBot () {
      return this.roomInfo.state === 'withBot'
    }
  },
  created () {
    this.special[this.keys.backspace] = true
    this.special[this.keys.shift] = true
    this.special[this.keys.ctrl] = true
    this.special[this.keys.alt] = true
    this.special[this.keys.delete] = true

    this.navigational[this.keys.upArrow] = true
    this.navigational[this.keys.downArrow] = true
    this.navigational[this.keys.leftArrow] = true
    this.navigational[this.keys.rightArrow] = true
  },
  data: () => ({
    audio: null,
    availableStates: ['ended', 'readyForAdvisor', 'registered', 'withBot'],
    keys: {
      backspace: 8,
      shift: 16,
      ctrl: 17,
      alt: 18,
      delete: 46,
      leftArrow: 37,
      upArrow: 38,
      rightArrow: 39,
      downArrow: 40
    },
    menuQuickResponses: false,
    navigational: {},
    recording: false,
    searchQuickResponses: '',
    showEmojis: false,
    settings: {
      maxLen: 2000
    },
    special: {},
    textLength: 0
  }),
  methods: {
    isNavigational (e) {
      return typeof this.navigational[e.keyCode] !== 'undefined'
    },
    isSpecial (e) {
      return typeof this.special[e.keyCode] !== 'undefined'
    },
    onPickEmoji (emoji) {
      this.$refs.messageEditor.innerText += emoji
    },
    sendMessage (message) {
      this.menuQuickResponses = false
      const text = message || this.$refs.messageEditor.innerText
      if (text === '') return

      this.$store.dispatch('sendMessage', { text })
      this.message = ''

      this.$refs.messageEditor.innerText = ''
      this.searchQuickResponses = ''
      this.$set(this, 'textLength', 0)
      this.$store.commit('REMOVE_DRAFT', this.selectedRoom)

      EventBus.$emit('conversationsUpdateRoomLastMessageSender')
    },
    sendMediaMessage (media, mimetype) {
      this.$store.dispatch('sendMessage', { media, mimetype, text: '' })
    },
    pasteText (event) {
      event.preventDefault()
      const text = (event.clipboardData || window.clipboardData).getData('text')
      const formattedText = text.replace(/\n\s*\n\s*\n/g, '\n')
      const sel = window.getSelection()
      if (!sel.rangeCount) return false
      sel.deleteFromDocument()
      sel.getRangeAt(0).insertNode(document.createTextNode(formattedText))

      if (window.getSelection) {
        if (sel.empty) {
          // Chrome
          var range = document.createRange()
          const el = this.$refs.messageEditor
          range.selectNodeContents(el)
          range.collapse(false)
          sel.removeAllRanges()
          sel.addRange(range)
        } else if (sel.removeAllRanges) {
          // Firefox
          sel.removeAllRanges()
        }
      }
    },
    async takeConversation () {
      try {
        if (this.mustSendTemplate) {
          const data = {
            country: this.userInfo.country || this.roomInfo.user.country,
            phone: this.userInfo.phone || this.roomInfo.user.phone,
            id: ''
          }

          this.$store.commit('SET_INIT_CONVERSATION', data)

          EventBus.$emit('conversationsOpenAutoStarts', 'smooch_wp')
          return
        }
        await this.$store.dispatch('takeConversation')
        EventBus.$emit('conversationsChangeFilter', 'withAdvisor')
      } catch (e) {
        notifications.error(e.message)
      }
    },
    async uploadAudio () {
      this.uploadProgress = 0
      const response = await conversationsService.uploadAudio(
        this.$store.getters.token,
        this.$store.getters.companyUuid,
        this.$store.getters.appUuid,
        this.selectedRoom,
        this.audio,
        this.updateProgress
      )
      this.sendMediaMessage(response.url, response.mimetype)
    },
    updateTextLength (event) {
      this.$set(this, 'textLength', event.target.innerText.trim().length)
    },
    checkKeyDown (event) {
      let hasSelection = false
      const selection = window.getSelection()
      const isSpecial = this.isSpecial(event)
      const isNavigational = this.isNavigational(event)

      if (selection) hasSelection = !!selection.toString()

      if (isSpecial || isNavigational) return

      if (this.textLength >= this.settings.maxLen && !hasSelection) event.preventDefault()
    }
  },
  watch: {
    selectedRoom (newValue, oldValue) {
      this.$store.commit('ADD_DRAFT', {
        room: oldValue,
        text: this.$refs.messageEditor.innerText
      })

      const draft = this.drafts.find(item => item.room === newValue)

      if (draft && draft.text && draft.text.length !== 0) {
        this.$refs.messageEditor.innerText = draft.text
        this.$set(this, 'textLength', this.$refs.messageEditor.innerText.trim().length)
      } else {
        this.$refs.messageEditor.innerText = ''
        this.$set(this, 'textLength', 0)
      }
    }
  }
}
</script>

<style>
.message-composer-container {
  align-items: center;
  background-color: #ffffff;
  border: solid 1px rgba(45, 206, 152, 0.2);
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  margin: 12px;
  min-height: 54px;
}

.message-composer__button-actions {
  background-color: #00b48e;
  cursor: pointer;
  font-weight: bold;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  min-height: 65px;
  color: white;
  align-items: center;
  justify-content: center;
  display: flex;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently */
}

.message-composer__error {
  border: solid 1px #00b48e;
  cursor: not-allowed;
  font-weight: bold;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  min-height: 65px;
  color: black;
  align-items: center;
  justify-content: center;
  display: flex;
  -webkit-touch-callout: none; /* iOS Safari */
  -webkit-user-select: none; /* Safari */
  -khtml-user-select: none; /* Konqueror HTML */
  -moz-user-select: none; /* Old versions of Firefox */
  -ms-user-select: none; /* Internet Explorer/Edge */
  user-select: none; /* Non-prefixed version, currently */
}
.noWritingAllow {
  visibility: hidden;
}

.kb-textarea-container {
  flex: 1 1 auto;
  flex-grow: 1;
  min-width: 0;
  padding: 9px 0 9px 9px;
}

.kb-textarea-container [contentEditable="true"]:empty:before {
  content: attr(data-ph);
  color: rgba(0, 0, 0, 0.3);
  cursor: text;
}

.kb-textarea-container [contentEditable="false"]:empty:before {
  content: attr(data-ph);
  color: #c92239;
  cursor: not-allowed;
}

.kb-textarea {
  font-size: 14px;
  line-height: 20px;
  max-height: 100px;
  max-width: 100%;
  min-height: 24px;
  padding: 6px 12px;
  overflow-x: hidden;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.kb-textarea:focus {
  outline: none;
}

.kb-textarea::-webkit-scrollbar {
  width: 4px;
}

.kb-textarea::-webkit-scrollbar-track {
  background: #cdefe8;
}

.kb-textarea::-webkit-scrollbar-thumb {
  background: #00b48e;
  border-radius: 2px;
}

.kb-textarea::-webkit-scrollbar-thumb:hover {
  background-color: #00b48e;
}
</style>
